import { useEffect, useRef, useSyncExternalStore } from 'react';

/**
 * How a page tells the global header's Refresh button what "refresh" means for
 * it. A page calls usePageRefresh(handler) while it is mounted; the header
 * button (account header: AccountShell, project header: Header) calls
 * runPageRefresh(). No page draws its own Refresh button in a bar.
 *
 * The most recently mounted page wins, so a form opened over a list refreshes
 * the form's data and the list comes back when the form closes.
 */
export type PageRefreshHandler = () => void | Promise<void>;

interface Entry {
  readonly run: () => void | Promise<void>;
  readonly busy: boolean;
}
export interface PageRefreshState {
  /** A mounted page has registered a handler. */
  readonly available: boolean;
  /** A refresh is running, or the page says it is loading. */
  readonly busy: boolean;
}

const IDLE: PageRefreshState = { available: false, busy: false };
let entries: readonly Entry[] = [];
let running = false;
let snapshot: PageRefreshState = IDLE;
const listeners = new Set<() => void>();

function publish(): void {
  const top = entries[entries.length - 1];
  const next: PageRefreshState = top ? { available: true, busy: top.busy || running } : { available: false, busy: running };
  if (next.available === snapshot.available && next.busy === snapshot.busy) return;
  snapshot = next;
  listeners.forEach(l => l());
}

const subscribe = (l: () => void): (() => void) => {
  listeners.add(l);
  return () => { listeners.delete(l); };
};

export const usePageRefreshState = (): PageRefreshState => useSyncExternalStore(subscribe, () => snapshot, () => IDLE);

/** Runs the current page's handler. Resolves false when no page registered one. A failing handler is logged, not thrown: pages report their own errors. */
export async function runPageRefresh(): Promise<boolean> {
  const top = entries[entries.length - 1];
  if (!top || running) return !!top;
  running = true;
  publish();
  try {
    await top.run();
  } catch (error) {
    console.error('Page refresh failed', error);
  } finally {
    running = false;
    publish();
  }
  return true;
}

/** Registers the page's refresh while it is mounted. `busy` greys the header button while the page itself is loading. */
export function usePageRefresh(handler: PageRefreshHandler, busy = false): void {
  const handlerRef = useRef(handler);
  handlerRef.current = handler;
  const entryRef = useRef<Entry | null>(null);

  useEffect(() => {
    const entry: Entry = { run: () => handlerRef.current(), busy: false };
    entryRef.current = entry;
    entries = [...entries, entry];
    publish();
    return () => {
      const mine = entryRef.current;
      entries = entries.filter(e => e !== mine);
      entryRef.current = null;
      publish();
    };
  }, []);

  useEffect(() => {
    const current = entryRef.current;
    if (!current || current.busy === busy) return;
    const next: Entry = { ...current, busy };
    entries = entries.map(e => (e === current ? next : e));
    entryRef.current = next;
    publish();
  }, [busy]);
}
